import { useCallback, useEffect, useState } from 'react';
import { pdApi, type PdAttributeLevel } from '../api';
import { HelpPanel } from '../HelpPanel';
import {
  BUSINESS_VALUE_CRITERIA,
  STRATEGIC_ALIGNMENT,
  BENEFIT_COST,
  DELIVERY_RISK,
} from '../helpContent';
import '../screens.css';
import './OpportunityAttributes.css';

/**
 * Settings → Opportunity Attributes.
 *
 * ONE SCREEN WHERE THERE WERE FOUR. Business Value Criteria, Strategic Alignment,
 * Benefit / Cost Effectiveness and Delivery Risk were four routes, four nav entries and
 * four near-identical files; they are one screen with four tabs, built to the prototype the
 * owner approved on 2026-08-09.
 *
 * This is NOT the refactor CLAUDE.md §1.2.1 forbids. That rule protects a PORT from being
 * "improved" into a shape the source never had. This is commissioned work: the owner asked
 * for the consolidation, reviewed a prototype and approved it. The four source screens are
 * unchanged in the source app.
 *
 * WHAT THE ATTRIBUTES DO NOT SHARE is declared per tab rather than flattened, because that
 * difference is the whole reason they were four files:
 *   - each has its own extra fields (business definition and characteristics; payback period
 *     and benefit multiple; risk indicators),
 *   - each has its own help panel, driven off the active tab,
 *   - Delivery Risk's scale is inverted — 5 is the worst — which the subtitle says out loud
 *     because the number alone reads the wrong way round.
 *
 * COLOURS ARE STORED BUT NOT SHOWN. Every level still carries `color_code`, and it still
 * drives the swatches on the opportunity screens; this screen neither displays nor edits it.
 * The owner's ruling: "these colors will stay assigned ... but they will not be visible".
 *
 * NO ACTIVE FLAG. `is_active` remains in the tables and is written as 1 on every save; the
 * screen does not offer it. His words: "it will be always active so there's no need".
 *
 * THE SCALE IS FIXED AT 1..5. A level can be removed and added back, which the source could
 * do neither of, but there is no sixth: the optimizer divides every attribute score by 5 and
 * `opportunities` carries its own 1..5 checks, so a sixth would silently unbalance portfolio
 * scoring. The server enforces the same bound and refuses to delete a level that
 * opportunities are still scored against.
 */

const MAX_LEVELS = 5;

/**
 * THE SCORE NUMBER IS COLOURED, dark green through red — the owner asked for the same
 * reading the rest of the scoring screens give (2026-08-09).
 *
 * EACH ATTRIBUTE KEEPS ITS OWN SENSE OF DIRECTION, which is the point. Alignment and
 * Benefit / Cost run green at 5, because 5 is good. DELIVERY RISK IS INVERTED — 5 is red,
 * because a score of 5 means very high risk, and a shared palette would colour the riskiest
 * band green and read as its exact opposite. That inversion is the original reason these
 * were four separate screens; it survives the consolidation here rather than being
 * flattened.
 *
 * BUSINESS VALUE CRITERIA IS INVERTED TOO, and for its own reason: 5 is mission-critical,
 * which is what the business cannot survive losing, so it reads red — the same direction its
 * stored colours have always run.
 *
 * THE NUMBER'S COLOUR IS DERIVED, NOT READ FROM `color_code`. Criticality's stored ramp is
 * red, orange, BLUE, amber, green — a blue in the middle of a five-step scale, inherited
 * from the source seed. Rendering it made the one thing on the row that is meant to read as
 * a score break its own gradient. The stored colour is untouched and still drives the
 * opportunity screens; this is only what the number is painted with.
 */
const SCORE_COLORS: Record<string, Record<number, string>> = {
  'business-criticality': { 5: '#E53935', 4: '#FB8C00', 3: '#FDD835', 2: '#43A047', 1: '#2E7D32' },
  'ai-alignment': { 5: '#2E7D32', 4: '#43A047', 3: '#FDD835', 2: '#FB8C00', 1: '#E53935' },
  'benefit-cost-effectiveness': { 5: '#2E7D32', 4: '#43A047', 3: '#FDD835', 2: '#FB8C00', 1: '#E53935' },
  'delivery-risk': { 5: '#E53935', 4: '#FB8C00', 3: '#FDD835', 2: '#43A047', 1: '#2E7D32' },
};

interface FieldSpec {
  key: string;
  label: string;
  type: 'text' | 'area';
  rows?: number;
  /** Half-width — two of these sit side by side. */
  half?: boolean;
}

interface AttributeSpec {
  key: string;
  tab: string;
  title: string;
  subtitle: string;
  fields: FieldSpec[];
  help: typeof BUSINESS_VALUE_CRITERIA;
}

/** The four, in the order the nav listed them. */
const ATTRIBUTES: AttributeSpec[] = [
  {
    key: 'business-criticality',
    tab: 'Business Value Criteria',
    title: 'Criticality levels',
    subtitle: 'How much the business depends on the opportunity.',
    fields: [
      { key: 'business_definition', label: 'Business definition', type: 'area', rows: 3 },
      { key: 'typical_characteristics', label: 'Typical characteristics', type: 'area', rows: 4 },
    ],
    help: BUSINESS_VALUE_CRITERIA,
  },
  {
    key: 'ai-alignment',
    tab: 'Strategic Alignment',
    title: 'Strategic alignment levels',
    subtitle: 'How well the opportunity fits stated strategy.',
    fields: [{ key: 'description', label: 'Description', type: 'area', rows: 2 }],
    help: STRATEGIC_ALIGNMENT,
  },
  {
    key: 'benefit-cost-effectiveness',
    tab: 'Benefit / Cost Effectiveness',
    title: 'Benefit / cost effectiveness levels',
    subtitle: 'Value returned per unit of spend.',
    fields: [
      { key: 'description', label: 'Description', type: 'area', rows: 2 },
      { key: 'payback_period', label: 'Payback period', type: 'text', half: true },
      { key: 'benefit_multiple', label: 'Benefit multiple', type: 'text', half: true },
    ],
    help: BENEFIT_COST,
  },
  {
    key: 'delivery-risk',
    tab: 'Delivery Risk',
    title: 'Delivery risk levels',
    subtitle: 'Likelihood the delivery runs into trouble. This scale is inverted — 5 is the worst.',
    fields: [
      { key: 'description', label: 'Description', type: 'area', rows: 2 },
      { key: 'risk_indicators', label: 'Risk indicators', type: 'area', rows: 2 },
    ],
    help: DELIVERY_RISK,
  },
];

type Row = PdAttributeLevel & Record<string, unknown>;

export function OpportunityAttributes() {
  const [tab, setTab] = useState(0);
  /* Measured, per CLAUDE.md "A screen header never scrolls" — this shell does not bound its
     content area, so `height: 100%` would resolve against nothing. */
  const [rootEl, setRootEl] = useState<HTMLDivElement | null>(null);
  const [rootH, setRootH] = useState<number | null>(null);
  const [rows, setRows] = useState<Record<string, Row[]>>({});
  /** Which tabs hold unsaved edits — the tab strip marks them. */
  const [dirty, setDirty] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const spec = ATTRIBUTES[tab]!;
  const levels = rows[spec.key] ?? [];

  const loadOne = useCallback(async (key: string) => {
    const d = await pdApi.settings.attributes(key);
    setRows((cur) => ({ ...cur, [key]: (d.levels ?? []) as Row[] }));
  }, []);

  /* All four load up front: the tabs then switch instantly and the counts are honest
     before a tab has been opened. Four small reads, once. */
  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all(ATTRIBUTES.map((a) => loadOne(a.key)));
      setDirty({});
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [loadOne]);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  useEffect(() => {
    if (!rootEl) return;
    const main = rootEl.closest('main');
    const measure = () => {
      const top = rootEl.getBoundingClientRect().top;
      const inset = main ? parseFloat(getComputedStyle(main).paddingBottom) || 0 : 0;
      const next = Math.max(320, Math.floor(window.innerHeight - top - inset));
      setRootH((prev) => (prev !== null && Math.abs(prev - next) < 2 ? prev : next));
    };
    measure();
    window.addEventListener('resize', measure);
    const ro = new ResizeObserver(measure);
    if (main) ro.observe(main);
    return () => {
      window.removeEventListener('resize', measure);
      ro.disconnect();
    };
  }, [rootEl]);

  const edit = (score: number, key: string, value: string) => {
    setRows((cur) => ({
      ...cur,
      [spec.key]: (cur[spec.key] ?? []).map((l) => (l.score === score ? { ...l, [key]: value } : l)),
    }));
    setDirty((d) => ({ ...d, [spec.key]: true }));
    setNotice(null);
  };

  const freeScores = () => {
    const used = new Set(levels.map((l) => Number(l.score)));
    return [1, 2, 3, 4, 5].filter((s) => !used.has(s));
  };

  const addLevel = async () => {
    const free = freeScores();
    if (!free.length) return;
    // The lowest free score: removing 2 and adding again puts 2 back, not 6.
    const score = free[free.length - 1]!;
    setError(null);
    setNotice(null);
    try {
      await pdApi.settings.createAttributeLevel(spec.key, score, {});
      await loadOne(spec.key);
      setNotice(`Level ${score} added. Give it a label and save.`);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const removeLevel = async (score: number) => {
    const lv = levels.find((l) => Number(l.score) === score);
    if (
      !window.confirm(
        `Remove level ${score} — “${String(lv?.label ?? '')}”?\n\n` +
          `It stops being offered when scoring an opportunity. If any opportunity is still ` +
          `scored against it, the removal will be refused and you will be told how many.`,
      )
    ) {
      return;
    }
    setError(null);
    setNotice(null);
    try {
      await pdApi.settings.deleteAttributeLevel(spec.key, score);
      await loadOne(spec.key);
      setNotice(`Level ${score} removed.`);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  /* Save writes every level of the ACTIVE tab. The endpoint is per level, so this is N
     small updates; four or five of them, on an explicit button press. */
  const save = async () => {
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      for (const lv of levels) {
        const body: Record<string, unknown> = { is_active: 1 };
        body.label = lv.label ?? '';
        for (const f of spec.fields) body[f.key] = (lv as Record<string, unknown>)[f.key] ?? '';
        // Carried through untouched: the screen does not show it, but the column is NOT NULL
        // on criticality and the update writes every declared field.
        if (spec.key === 'business-criticality') body.color_code = lv.color_code ?? '#1976d2';
        await pdApi.settings.updateAttribute(spec.key, Number(lv.score), body);
      }
      setDirty((d) => ({ ...d, [spec.key]: false }));
      setNotice(`${spec.tab} saved.`);
      await loadOne(spec.key);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const discard = async () => {
    if (dirty[spec.key] && !window.confirm('Discard unsaved changes on this tab?')) return;
    setError(null);
    setNotice(null);
    await loadOne(spec.key);
    setDirty((d) => ({ ...d, [spec.key]: false }));
  };

  const levelColour = (key: string, lv: Row) =>
    SCORE_COLORS[key]?.[Number(lv.score)] ?? '#64748b';

  const dirtyCount = Object.values(dirty).filter(Boolean).length;
  const full = levels.length >= MAX_LEVELS;

  return (
    <div className="oa-screen" ref={setRootEl} style={rootH ? { height: rootH } : undefined}>
      <div className="pd-page-header">
        <div>
          <h1 className="pd-page-header__title">Opportunity Attributes</h1>
          <p className="pd-page-header__subtitle">
            The four scales an opportunity is judged on. Edit the wording in place; add or
            remove levels within the 1–5 range.
          </p>
        </div>
        {/* Help follows the tab — each attribute keeps its own panel. Save and Discard sit
            here rather than in a footer, which the owner could not see. */}
        <div className="pd-page-header__actions">
          <HelpPanel helps={ATTRIBUTES.map((a) => ({ ...a.help, tabLabel: a.tab }))} activeIndex={tab} />
        </div>
      </div>

      {error ? <div className="pd-error">{error}</div> : null}
      {notice ? <div className="pd-notice">{notice}</div> : null}

      <div className="oa-tabs">
        {ATTRIBUTES.map((a, i) => (
          <button
            key={a.key}
            type="button"
            className={`oa-tab${i === tab ? ' on' : ''}`}
            onClick={() => setTab(i)}
          >
            {a.tab}
            {dirty[a.key] ? <span className="oa-dot" title="unsaved changes" /> : null}
          </button>
        ))}
      </div>

      <div className="oa-bar">
        <div>
          <h2 className="oa-bar-title">{spec.title}</h2>
          <div className="oa-bar-sub">
            {levels.length} of {MAX_LEVELS} · {spec.subtitle}
          </div>
        </div>
        {/*
          Save, Discard and the change state sit on THIS row — level with the section title
          and its description — rather than up in the header (owner, 2026-08-09).
        */}
        <div className="oa-bar-actions">
          <span className={`oa-state${dirtyCount ? ' on' : ''}`}>
            {dirtyCount
              ? `Unsaved changes in ${dirtyCount} attribute${dirtyCount > 1 ? 's' : ''}`
              : 'No changes'}
          </span>
          <button type="button" className="oa-btn" onClick={() => void addLevel()} disabled={full || loading}>
            Add level
          </button>
          <button type="button" className="oa-btn" onClick={() => void discard()} disabled={saving}>
            Discard
          </button>
          <button
            type="button"
            className="oa-btn oa-btn--primary"
            onClick={() => void save()}
            disabled={saving || loading}
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="pd-loading">Loading…</div>
      ) : (
        <>
          <div className="oa-levels oa-levels--scroll">
            {levels.length === 0 ? (
              <div className="oa-empty">
                No levels defined. Use <b>Add level</b> to build the scale.
              </div>
            ) : (
              levels
                .slice()
                .sort((a, b) => Number(b.score) - Number(a.score))
                .map((lv) => {
                  const score = Number(lv.score);
                  const halves = spec.fields.filter((f) => f.half);
                  const rest = spec.fields.filter((f) => !f.half);
                  const field = (f: FieldSpec) => (
                    <div className="oa-fld" key={f.key}>
                      <label htmlFor={`oa-${spec.key}-${score}-${f.key}`}>{f.label}</label>
                      {f.type === 'area' ? (
                        <textarea
                          id={`oa-${spec.key}-${score}-${f.key}`}
                          className="oa-ta"
                          rows={f.rows ?? 2}
                          value={String((lv as Record<string, unknown>)[f.key] ?? '')}
                          onChange={(e) => edit(score, f.key, e.target.value)}
                        />
                      ) : (
                        <input
                          id={`oa-${spec.key}-${score}-${f.key}`}
                          className="oa-in"
                          type="text"
                          value={String((lv as Record<string, unknown>)[f.key] ?? '')}
                          onChange={(e) => edit(score, f.key, e.target.value)}
                        />
                      )}
                    </div>
                  );
                  return (
                    <div className="oa-lv" key={score}>
                      <div className="oa-lv-score">
                        <div className="oa-lv-n" style={{ color: levelColour(spec.key, lv) }}>
                          {score}
                        </div>
                        <div className="oa-lv-of">of {MAX_LEVELS}</div>
                      </div>
                      <div className="oa-lv-body">
                        <div className="oa-fld">
                          <label htmlFor={`oa-${spec.key}-${score}-label`}>Label</label>
                          <input
                            id={`oa-${spec.key}-${score}-label`}
                            className="oa-in oa-in--title"
                            type="text"
                            value={String(lv.label ?? '')}
                            onChange={(e) => edit(score, 'label', e.target.value)}
                          />
                        </div>
                        {rest.map(field)}
                        {halves.length ? <div className="oa-pair">{halves.map(field)}</div> : null}
                      </div>
                      <div className="oa-lv-actions">
                        {/* Was a pale ✕ that could barely be seen. It now reads as a
                            control and turns red on hover (owner, 2026-08-09). */}
                        <button
                          type="button"
                          className="oa-remove"
                          title={`Remove level ${score}`}
                          aria-label={`Remove level ${score}`}
                          onClick={() => void removeLevel(score)}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  );
                })
            )}
          </div>

          <div className="oa-foot">
            <span className="oa-why">
              {full
                ? `All ${MAX_LEVELS} levels are defined. The scale is fixed at 1–5 — remove one to free a slot.`
                : `Free: ${freeScores().join(', ')}. Adding restores that score.`}
            </span>
          </div>
        </>
      )}
    </div>
  );
}
